import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAktieDetail,
  fetchAktieLive,
  selectAktie,
  selectAktieDetail,
  selectAktieDetailStatus,
  selectAktieLive,
  selectAktieLiveStatus,
} from 'modules/aktien/features/aktienDbSlice';
import {
  formatKurs,
  formatProz,
  formatDatum,
  formatVeraenderung,
  formatZahl,
  formatKompakt,
} from 'core/utils/format';
import { reloadHistoryApi } from 'modules/aktien/features/aktienApi';

/** Eine Kennzahl-Kachel (Label + Wert) für die Stammdaten. */
function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-line bg-paper-soft px-4 py-3">
      <div className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-medium text-ink">
        {value || '–'}
      </div>
    </div>
  );
}

/** Prozentwert ohne Vorzeichen (z.B. Rendite/ROE). Null -> „–". */
const formatProzRoh = (value) =>
  value == null ? '–' : `${formatZahl(value, 1)} %`;

/**
 * Hervorgehobenes Kursfeld: großer aktueller Kurs + Tagesentwicklung.
 *
 * Bevorzugt den Live-Kurs (OnVista); fällt auf den zuletzt gespeicherten
 * Tageswert zurück, solange der Live-Abruf läuft oder fehlschlägt.
 */
function KursFeld({ live, liveStatus, gespeichert, gespeichertAbs, currency }) {
  const istLive = liveStatus === 'succeeded' && live;
  const kurs = istLive ? live : gespeichert;

  const kursWert = kurs?.aktuellPreis ?? kurs?.schlusskurs ?? null;
  const aenderungProz = istLive
    ? live?.aenderungProz
    : gespeichert?.entwicklung ?? gespeichert?.aenderungProz;
  const veraenderungAbs = istLive ? live?.performance : gespeichertAbs;

  const positiv = (aenderungProz ?? 0) >= 0;

  const quelleLabel =
    liveStatus === 'loading'
      ? 'wird geladen…'
      : istLive
      ? 'Live · OnVista'
      : 'zuletzt gespeichert';

  const standLabel = (() => {
    if (!kurs) return null;
    const datum = formatDatum(kurs.datum);
    if (istLive && kurs.abrufzeit) {
      return `${datum}, ${String(kurs.abrufzeit).slice(0, 5)} Uhr`;
    }
    return datum;
  })();

  return (
    <section className="mb-8 rounded-2xl border border-line bg-paper-soft p-6 shadow-sm sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
            Aktueller Kurs · {quelleLabel}
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-4xl font-semibold tabular-nums text-ink sm:text-5xl">
              {formatKurs(kursWert, currency)}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 font-mono text-sm font-semibold tabular-nums ${
                aenderungProz == null
                  ? 'bg-ink/5 text-ink-muted'
                  : positiv
                  ? 'bg-gain/10 text-gain'
                  : 'bg-loss/10 text-loss'
              }`}
            >
              {formatProz(aenderungProz)}
            </span>
          </div>
          <div className="mt-2 text-sm text-ink-muted">
            {veraenderungAbs != null && (
              <span className={positiv ? 'text-gain' : 'text-loss'}>
                {formatVeraenderung(veraenderungAbs, currency)}
              </span>
            )}
            {veraenderungAbs != null && ' heute'}
            {standLabel && (
              <>
                {veraenderungAbs != null ? ' · ' : ''}
                Stand {standLabel}
              </>
            )}
          </div>
        </div>

        {/* Eröffnung / Hoch / Tief des Tages */}
        {kurs && (
          <dl className="grid grid-cols-3 gap-x-6 gap-y-1 text-right">
            {[
              ['Eröffnung', kurs.eroeffen],
              ['Hoch', kurs.hoch],
              ['Tief', kurs.tief],
            ].map(([label, wert]) => (
              <div key={label}>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                  {label}
                </dt>
                <dd className="font-mono tabular-nums text-sm text-ink-soft">
                  {formatKurs(wert, currency)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

/** Reiter-Button der Tab-Navigation. */
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? 'border-ink text-ink'
          : 'border-transparent text-ink-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** Leerer-Zustand-Hinweis innerhalb eines Tabs. */
function LeerHinweis({ children }) {
  return (
    <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
      {children}
    </p>
  );
}

/** Spaltenkopf einer Tabelle. */
const Th = ({ children, right }) => (
  <th className={`px-4 py-3 font-medium ${right ? 'text-right' : ''}`}>{children}</th>
);

/**
 * Reiter „Aktie Details": oben Name + hervorgehobener Live-Kurs, darunter die
 * Stammdaten, darunter Tabs mit Tages-, Jahresübersicht und Dividende.
 *
 * Der Zurück-Button führt zur „Liste DB"; der dort gemerkte Suchbegriff bleibt
 * erhalten (liegt im Redux-State).
 */
export default function AktieDetailView() {
  const { isin } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const detail = useSelector(selectAktieDetail);
  const status = useSelector(selectAktieDetailStatus);
  const live = useSelector(selectAktieLive);
  const liveStatus = useSelector(selectAktieLiveStatus);

  const [tab, setTab] = useState('tag');
  // Status des manuellen History-Nachladens: idle | loading | done | error.
  const [historieStatus, setHistorieStatus] = useState('idle');
  const [historieMeldung, setHistorieMeldung] = useState(null);

  // Historische Werte erneut von OnVista abrufen und die Detailansicht neu laden.
  const handleHistorieNachladen = async () => {
    setHistorieStatus('loading');
    setHistorieMeldung(null);
    try {
      const neu = await reloadHistoryApi(isin);
      setHistorieStatus('done');
      setHistorieMeldung(
        neu > 0
          ? `${neu} fehlende Tageswerte ergänzt.`
          : 'Keine fehlenden Tageswerte – History ist aktuell.'
      );
      dispatch(fetchAktieDetail(isin));
    } catch (err) {
      setHistorieStatus('error');
      setHistorieMeldung('Historische Werte konnten nicht abgerufen werden.');
    }
  };

  // Detail (DB) + Live-Kurs (OnVista) laden, sobald sich die ISIN ändert; ISIN als
  // Auswahl merken (hält den Menü-Reiter „Aktie Details" aktiv, auch bei direktem Aufruf).
  useEffect(() => {
    if (isin) {
      dispatch(selectAktie(isin));
      dispatch(fetchAktieDetail(isin));
      dispatch(fetchAktieLive(isin));
    }
  }, [dispatch, isin]);

  const currency = detail?.currency;

  // Tageswerte kommen absteigend (neuester zuerst). Entwicklung = Veränderung
  // des Schlusskurses gegenüber dem nächstälteren gespeicherten Tag; fehlt der
  // Vergleichswert, greifen wir auf das gelieferte aenderungProz zurück.
  const tageswerte = useMemo(() => {
    const werte = detail?.tageswerte ?? [];
    return werte.map((t, i) => {
      const vorher = werte[i + 1];
      let entwicklung = t.aenderungProz != null ? Number(t.aenderungProz) : null;
      if (vorher?.schlusskurs != null && t.schlusskurs != null) {
        const prev = Number(vorher.schlusskurs);
        if (prev !== 0) {
          entwicklung = ((Number(t.schlusskurs) - prev) / prev) * 100;
        }
      }
      return { ...t, entwicklung };
    });
  }, [detail]);

  // Jahreswerte absteigend nach Jahr (neueste/Schätzungen zuerst).
  const jahreswerte = useMemo(() => {
    const werte = detail?.jahreswerte ?? [];
    return [...werte].sort((a, b) => (b.jahr ?? 0) - (a.jahr ?? 0));
  }, [detail]);

  // Absolute Tagesveränderung aus den gespeicherten Werten (Fallback fürs Kursfeld).
  const gespeichertAbs = useMemo(() => {
    const heute = tageswerte[0];
    const gestern = tageswerte[1];
    if (heute?.schlusskurs != null && gestern?.schlusskurs != null) {
      return Number(heute.schlusskurs) - Number(gestern.schlusskurs);
    }
    return null;
  }, [tageswerte]);

  return (
    <div>
      {/* Zurück zur Liste */}
      <button
        type="button"
        onClick={() => navigate('/projekte/aktienanalyse/liste')}
        className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
      >
        <span aria-hidden="true">←</span>
        Zurück zur Liste
      </button>

      {status === 'loading' && <p className="text-ink-muted">Lade Details…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Details konnten nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' && detail && (
        <>
          <header className="mb-6">
            <h1 className="font-serif text-4xl font-semibold tracking-tight text-ink">
              {detail.aktienName || detail.isin}
            </h1>
            <p className="mt-1 font-mono text-sm text-ink-muted">{detail.isin}</p>
          </header>

          {/* Hervorgehobenes Kursfeld (Live + Tagesentwicklung) */}
          <KursFeld
            live={live}
            liveStatus={liveStatus}
            gespeichert={tageswerte[0]}
            gespeichertAbs={gespeichertAbs}
            currency={currency}
          />

          {/* Stammdaten */}
          <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Stammdaten
          </h2>
          <div className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <Stat label="WKN" value={detail.wkn} />
            <Stat label="Symbol" value={detail.symbol} />
            <Stat label="Markt" value={detail.market} />
            <Stat label="Währung" value={detail.currency} />
            <Stat label="Land" value={detail.land} />
            <Stat label="Branche" value={detail.branche} />
            <Stat label="Sektor" value={detail.sektorName} />
            <Stat
              label="Marktkapitalisierung"
              value={
                detail.marketCapitalization != null
                  ? formatKompakt(detail.marketCapitalization, detail.currency)
                  : null
              }
            />
            <Stat
              label="Indizes"
              value={
                detail.indexNames && detail.indexNames.length > 0
                  ? detail.indexNames.join(', ')
                  : detail.indexName
              }
            />
          </div>

          {/* Tabs */}
          <div className="mb-5 flex gap-1 border-b border-line">
            <TabButton active={tab === 'tag'} onClick={() => setTab('tag')}>
              Tagesübersicht
            </TabButton>
            <TabButton active={tab === 'jahr'} onClick={() => setTab('jahr')}>
              Jahresübersicht
            </TabButton>
            <TabButton active={tab === 'dividende'} onClick={() => setTab('dividende')}>
              Dividende
            </TabButton>
          </div>

          {tab === 'tag' && (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleHistorieNachladen}
                  disabled={historieStatus === 'loading'}
                  className="inline-flex items-center gap-2 rounded-lg border border-line bg-paper px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span aria-hidden="true">↻</span>
                  {historieStatus === 'loading'
                    ? 'Historische Werte werden abgerufen…'
                    : 'Historische Werte erneut abrufen'}
                </button>
                {historieMeldung && (
                  <span
                    className={`text-sm ${
                      historieStatus === 'error' ? 'text-loss' : 'text-ink-muted'
                    }`}
                  >
                    {historieMeldung}
                  </span>
                )}
              </div>
              <TagesTabelle tageswerte={tageswerte} currency={currency} />
            </>
          )}
          {tab === 'jahr' && (
            <JahresTabelle jahreswerte={jahreswerte} currency={currency} />
          )}
          {tab === 'dividende' && (
            <DividendenTabelle jahreswerte={jahreswerte} currency={currency} />
          )}
        </>
      )}
    </div>
  );
}

/** Tab „Tagesübersicht": alle gespeicherten Tageswerte inkl. Entwicklung. */
function TagesTabelle({ tageswerte, currency }) {
  if (tageswerte.length === 0) {
    return <LeerHinweis>Für diese Aktie sind noch keine Tageswerte gespeichert.</LeerHinweis>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
            <Th>Datum</Th>
            <Th right>Eröffnung</Th>
            <Th right>Hoch</Th>
            <Th right>Tief</Th>
            <Th right>Schluss</Th>
            <Th right>Volumen</Th>
            <Th right>Entwicklung</Th>
          </tr>
        </thead>
        <tbody>
          {tageswerte.map((t) => {
            const positiv = (t.entwicklung ?? 0) >= 0;
            return (
              <tr
                key={t.uuid || t.datum}
                className="border-t border-line transition-colors hover:bg-ink/[0.03]"
              >
                <td className="px-4 py-3 text-ink-soft">{formatDatum(t.datum)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                  {formatKurs(t.eroeffen, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                  {formatKurs(t.hoch, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                  {formatKurs(t.tief, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono font-medium tabular-nums text-ink">
                  {formatKurs(t.schlusskurs, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-muted">
                  {t.volumen != null
                    ? new Intl.NumberFormat('de-DE').format(t.volumen)
                    : '–'}
                </td>
                <td
                  className={`px-4 py-3 text-right font-mono font-medium tabular-nums ${
                    t.entwicklung == null
                      ? 'text-ink-muted'
                      : positiv
                      ? 'text-gain'
                      : 'text-loss'
                  }`}
                >
                  {formatProz(t.entwicklung)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Tab „Jahresübersicht": mehrjährige Geschäftskennzahlen. */
function JahresTabelle({ jahreswerte, currency }) {
  if (jahreswerte.length === 0) {
    return <LeerHinweis>Für diese Aktie sind noch keine Jahreswerte gespeichert.</LeerHinweis>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
            <Th>Jahr</Th>
            <Th right>Umsatz</Th>
            <Th right>Jahresüberschuss</Th>
            <Th right>EPS</Th>
            <Th right>KGV</Th>
            <Th right>ROE</Th>
          </tr>
        </thead>
        <tbody>
          {jahreswerte.map((j) => (
            <tr
              key={j.uuid || j.jahr}
              className="border-t border-line transition-colors hover:bg-ink/[0.03]"
            >
              <td className="px-4 py-3 font-medium text-ink">{j.jahr ?? '–'}</td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatKompakt(j.umsatz, currency)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatKompakt(j.jahresueberschuss, currency)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatKurs(j.eps, currency)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatZahl(j.kgv, 1)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatProzRoh(j.roe)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Tab „Dividende": Dividende je Aktie und Dividendenrendite pro Jahr. */
function DividendenTabelle({ jahreswerte, currency }) {
  // Nur Jahre mit Dividenden-Information anzeigen.
  const mitDividende = jahreswerte.filter(
    (j) => j.dividende != null || j.dividendeRendite != null
  );
  if (mitDividende.length === 0) {
    return (
      <LeerHinweis>
        Für diese Aktie liegen keine Dividenden-Daten vor.
      </LeerHinweis>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
            <Th>Jahr</Th>
            <Th right>Dividende je Aktie</Th>
            <Th right>Dividendenrendite</Th>
          </tr>
        </thead>
        <tbody>
          {mitDividende.map((j) => (
            <tr
              key={j.uuid || j.jahr}
              className="border-t border-line transition-colors hover:bg-ink/[0.03]"
            >
              <td className="px-4 py-3 font-medium text-ink">{j.jahr ?? '–'}</td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                {formatKurs(j.dividende, currency)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                {formatProzRoh(j.dividendeRendite)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
